import { useEffect, Suspense } from 'react';
import { Box, CircularProgress, ThemeProvider } from '@mui/material';
import { AppRouter } from './routes/Router';
import { useAuthStore } from './store/authStore';
import { setUnauthorizedHandler } from './services/api/client';
import { registerWebPush } from './services/push/webPush';
import { useCurrentUserSync } from './services/queries/useCurrentUser';
import { theme } from './theme/theme';

function LoadingScreen() {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f5f5f5"
    >
      <CircularProgress />
    </Box>
  );
}

function App() {
  const initialize = useAuthStore((state) => state.initialize);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Keeps partner capabilities live without requiring a re-login (FR-053).
  useCurrentUserSync();

  useEffect(() => {
    // Initialize auth from localStorage
    initialize();

    // Set up 401 handler
    setUnauthorizedHandler((reason: 'SESSION_REVOKED' | 'TOKEN_EXPIRED' | 'OTHER') => {
      clearAuth();
      // Route to login is handled by ProtectedRoute
      if (reason === 'SESSION_REVOKED') {
        alert('Your session was ended by an administrator.');
      } else if (reason === 'TOKEN_EXPIRED') {
        alert('Your session has expired. Please log in again.');
      }
    });
  }, [initialize, clearAuth]);

  // Register for web push once authenticated (covers login + a rehydrated
  // session). Idempotent and self-guarding; no-ops until Firebase env is set.
  // Logout unregisters separately (in the logout mutation, token still valid).
  useEffect(() => {
    if (isAuthenticated) {
      registerWebPush();
    }
  }, [isAuthenticated]);

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Suspense fallback={<LoadingScreen />}>
        <AppRouter />
      </Suspense>
    </ThemeProvider>
  );
}

export default App;
